import { debounce, isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDataProvider } from '@work4all/data';

import { Contact } from '@work4all/models/lib/Classes/Contact.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';

import { useEMailSuggestions } from '../../../../../components/entity-picker/EMailSuggestionPickerField';
import { useFormContextPlus } from '../../../form-plus/use-form-context-plus';
import { useMaskContext } from '../../hooks/mask-context';

import { ContactMaskFormValue } from './types';

export const useContactEmailSuggestions = () => {
  const mask = useMaskContext();
  const { setValue } = useFormContextPlus<ContactMaskFormValue>();

  const [enableEMailSuggestions, setEnableEMailSuggestions] = useState(false);

  const debouncedEmailSuggestionRequest = useDebouncedEmailSuggestionRequest(
    enableEMailSuggestions
  );

  const type =
    mask.template.entity === Entities.customer
      ? SdObjType.KUNDE
      : SdObjType.LIEFERANT;

  const contactCountRequestData = useMemo(() => {
    const filter = [
      {
        businessPartnerType: {
          $eq: type,
        },
      },
      {
        businessPartnerId: {
          $eq: debouncedEmailSuggestionRequest?.businessPartnerId,
        },
      },
    ];

    const data: Contact = {
      id: null,
    };

    return {
      filter,
      entity: Entities.contact,
      data,
    };
  }, [type, debouncedEmailSuggestionRequest?.businessPartnerId]);

  const { distinctEmailSuggestion } = useEMailSuggestions(
    debouncedEmailSuggestionRequest
  );

  const { total: totalContacts } = useDataProvider<Contact>(
    contactCountRequestData
  );

  const handleEMailSuggestion = useCallback(() => {
    if (enableEMailSuggestions && distinctEmailSuggestion) {
      setValue('eMail', distinctEmailSuggestion, { shouldDirty: true });
    }
  }, [distinctEmailSuggestion, enableEMailSuggestions, setValue]);

  useEffect(() => {
    handleEMailSuggestion();
  }, [handleEMailSuggestion]);

  useEffect(() => {
    if (mask.id || totalContacts === 0) {
      setEnableEMailSuggestions(false);
    } else {
      setEnableEMailSuggestions(true);
    }
  }, [mask.id, totalContacts]);

  return {
    enableEMailSuggestions,
    setEnableEMailSuggestions,
    ...debouncedEmailSuggestionRequest,
  };
};

export const useDebouncedEmailSuggestionRequest = (
  enableEMailSuggestions: boolean
) => {
  const { watch } = useFormContextPlus<ContactMaskFormValue>();

  const prevValue = useRef<Parameters<typeof useEMailSuggestions>[0] | null>(
    null
  );

  const [state, setState] = useState<
    Parameters<typeof useEMailSuggestions>[0] | null
  >(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSet = useCallback(
    debounce((value) => setState(value), 300),
    []
  );

  useEffect(() => {
    const subscription = watch((inValue: ContactMaskFormValue) => {
      const newValue = {
        businessPartnerId: inValue.businessPartnerId,
        businesspartnerType: inValue.businessPartnerType,
        firstname: inValue.firstName,
        lastname: inValue.name,
        skip: !enableEMailSuggestions,
      };

      if (!isEqual(prevValue.current, newValue)) {
        debounceSet(newValue);
      }

      prevValue.current = newValue;
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [debounceSet, watch, enableEMailSuggestions]);

  return state || {};
};
